/**
 * Link-Zeile mit dem Uebertragungsstand des angezeigten Zeitraums (D-09, D-14; Sketch 025
 * `.lastline`/`renderEmbedded`). Text und Zustand kommen fertig vom Server (`getTransferState`,
 * 41-06) -- kein eigenes Datumsformat und keine eigene Satzbildung hier (CLAUDE.md: vom Server
 * gelieferte Werte nicht im Frontend nachbauen). "Verlauf" fuehrt in die Gesamtuebersicht mit
 * Auswertung + Zeitraum als Filter (`buildTransferOverviewSearch`, 41-06).
 *
 * Ein Abrufsfehler (allen voran 403 -- T-41-25: Personen ohne Lesefreischaltung fuer den Bereich)
 * rendert nichts. Die Zeile ist Zusatzinformation zur Auswertungsseite, kein eigenstaendiger
 * Fehlerzustand.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Anchor } from '@mantine/core';
import { Link } from 'react-router';
import { statistikPortalTransferApi } from '../../services/statistikPortalTransferApi';
import { buildTransferOverviewSearch } from '../../pages/transfers/transferOverviewUrl';
import { statusPalette } from '../../theme/colors';
import type { TransferCellStatus, TransferLinkState } from '../../types/statistikPortalTransfer';
import classes from './evaluationTable.module.css';

// Nur die drei Zustaende, die TransferCellStatus tatsaechlich kennt (transferred/changed/failed);
// "never" nutzt denselben grauen Punkt wie "offen" in der Periodenleiste (stripDotOpen), braucht
// deshalb keine eigene Farbvariable.
const transferLineColorVars = {
  '--strip-transferred-color': statusPalette.freigegeben.fg,
  '--strip-changed-color': statusPalette.eingereicht.fg,
  '--strip-failed-color': statusPalette.fehler.fg,
} as CSSProperties;

const DOT_CLASS: Record<TransferCellStatus, string> = {
  transferred: classes.stripDotTransferred,
  changed: classes.stripDotChanged,
  failed: classes.stripDotFailed,
  never: classes.stripDotOpen,
};

export interface TransferLinkRowProps {
  evaluationType: string;
  year: number;
  period: number;
  /** Erhoeht sich nach jeder Uebertragung/Sperrentscheidung -- loest einen erneuten Abruf aus. */
  refreshKey?: number;
}

export function TransferLinkRow({
  evaluationType,
  year,
  period,
  refreshKey,
}: TransferLinkRowProps) {
  const [state, setState] = useState<TransferLinkState | null>(null);

  useEffect(() => {
    let cancelled = false;

    statistikPortalTransferApi
      .getTransferState(evaluationType, year, period)
      .then((result) => {
        if (!cancelled) {
          setState(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [evaluationType, year, period, refreshKey]);

  if (!state) {
    return null;
  }

  return (
    <div
      className={classes.transferLine}
      data-status={state.status}
      style={transferLineColorVars}
    >
      <span className={`${classes.stripDot} ${DOT_CLASS[state.status]}`} />
      {state.linkLabel} ·{' '}
      <Anchor
        component={Link}
        to={'/transfers' + buildTransferOverviewSearch(state.overviewQuery)}
      >
        Verlauf
      </Anchor>
    </div>
  );
}
